import React, { useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, MapPin, Clock, Building2, CheckCircle, Link2, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import { locaties } from '../data/locaties';
import { heroData } from '../data/mockData';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Overzichtspagina: /locaties
export const LocatiesOverviewPage = () => (
  <main className="pt-28 min-h-screen">
    <SEO
      title="Locaties in heel Nederland – Staffing Force"
      description="Staffing Force werkt in heel Nederland. Bekijk onze service per stad: Amsterdam, Rotterdam, Utrecht, Den Haag, Eindhoven en 15 andere locaties voor Finance, HR en Duurzaamheidsprofessionals."
      keywords="staffing force locaties, detachering Nederland, interim controller per stad"
    />
    <PageHero
      label="Heel Nederland"
      title="Onze locaties"
      description="Hoofdkantoor in Amsterdam, opdrachten in heel Nederland. Bekijk per stad waar wij actief zijn en welke sectoren we daar bedienen."
      image="https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1920&q=85&auto=format&fit=crop"
    />
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        <FadeIn>
          <p className="text-gray-700 leading-relaxed text-base md:text-lg max-w-3xl mb-12">
            Vanuit ons hoofdkantoor aan de Johan Huizingalaan 400 in Amsterdam bedienen wij opdrachtgevers in
            heel Nederland. Onderstaand een overzicht van de steden en regio's waar Staffing Force actief is.
            Klik op een locatie voor uitgebreide marktinformatie, sectoren en lokale context.
          </p>
        </FadeIn>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {locaties.map((loc, i) => (
            <FadeIn key={loc.slug} delay={i * 0.025}>
              <Link
                to={`/locaties/${loc.slug}`}
                className="group block bg-gray-50 hover:bg-[#00897B]/5 border border-gray-100 hover:border-[#00897B]/30 rounded-xl p-4 md:p-5 transition-all"
              >
                <MapPin size={18} className="text-[#00897B] mb-2" />
                <h3 className="font-semibold text-gray-900 group-hover:text-[#00897B] transition-colors">
                  {loc.stad}
                </h3>
                <p className="text-xs text-gray-500 mt-1">{loc.provincie}</p>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  </main>
);

// FAQ-accordeon component
const FaqItem = ({ vraag, antwoord, isOpen, onToggle }) => (
  <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full text-left px-5 md:px-6 py-4 md:py-5 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
    >
      <h3 className="font-semibold text-gray-900 text-sm md:text-base">{vraag}</h3>
      <ChevronDown
        size={20}
        className={`text-[#00897B] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    {isOpen && (
      <div className="px-5 md:px-6 pb-5 md:pb-6 text-gray-700 leading-relaxed text-sm md:text-base">
        {antwoord}
      </div>
    )}
  </div>
);

// Detailpagina: /locaties/:slug
export const LocatiePage = () => {
  const { slug } = useParams();
  const loc = locaties.find((l) => l.slug === slug);
  const [openFaq, setOpenFaq] = useState(null);

  if (!loc) return <Navigate to="/locaties" replace />;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Detachering Finance, HR en Duurzaamheid',
    provider: {
      '@type': 'EmploymentAgency',
      name: 'Staffing Force',
      url: 'https://www.staffingforce.nl',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Johan Huizingalaan 400',
        addressLocality: 'Amsterdam',
        addressRegion: 'Noord-Holland',
        postalCode: '1066 JS',
        addressCountry: 'NL',
      },
      telephone: '+31205811000',
    },
    areaServed: {
      '@type': 'City',
      name: loc.stad,
      containedInPlace: { '@type': 'AdministrativeArea', name: loc.provincie },
    },
    description: loc.inleiding,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: loc.faqs.map((f) => ({
      '@type': 'Question',
      name: f.vraag,
      acceptedAnswer: { '@type': 'Answer', text: f.antwoord },
    })),
  };

  return (
    <main className="min-h-screen">
      <SEO
        title={`${loc.h1} – Staffing Force`}
        description={`${loc.h1}. Interim controllers, HR Business Partners en CSRD consultants in ${loc.stad} en omgeving (${loc.provincie}). Shortlist binnen 48 tot 72 uur via Staffing Force.`}
        keywords={`detachering ${loc.stad}, interim controller ${loc.stad}, HR ${loc.stad}, finance professionals ${loc.stad.toLowerCase()}, staffing force ${loc.stad.toLowerCase()}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* HERO with VIDEO background (same as homepage) */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          {/* Mobile: static image for fast LCP */}
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=85&auto=format&fit=crop"
            alt={`Modern kantoor in ${loc.stad} – Staffing Force`}
            className="w-full h-full object-cover md:hidden"
            fetchpriority="high"
          />
          {/* Desktop: same background video as homepage */}
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=85&auto=format&fit=crop"
            aria-label={`Achtergrondvideo Staffing Force ${loc.stad}`}
            className="w-full h-full object-cover hidden md:block"
          >
            <source src={heroData.videoUrl} type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70 z-10" />
        <div className="relative z-20 h-full flex flex-col justify-center items-center px-4 md:px-6">
          <div className="text-center max-w-5xl w-full">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="inline-block text-[#00bfa5] text-xs md:text-sm font-semibold tracking-widest uppercase mb-4"
            >
              {loc.provincie}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight"
            >
              {loc.h1}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-white/85 text-sm sm:text-base md:text-lg mt-6 md:mt-8 max-w-3xl mx-auto px-2 leading-relaxed"
            >
              {loc.inleiding}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="flex flex-col sm:flex-row gap-3 justify-center mt-8 md:mt-10"
            >
              <Link
                to="/opdracht-opgeven"
                className="bg-[#00897B] hover:bg-[#00796B] text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              >
                Opdracht opgeven <ArrowRight size={16} />
              </Link>
              <Link
                to="/vacatures"
                className="border-2 border-white/40 hover:border-white text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all hover:-translate-y-0.5 inline-flex items-center justify-center"
              >
                Bekijk vacatures
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="py-12 md:py-16 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <div className="grid grid-cols-3 gap-4 md:gap-6">
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 md:p-6 text-center">
                <Clock size={20} className="text-[#00897B] mx-auto mb-2" />
                <div className="text-sm md:text-base font-bold text-gray-900">48 tot 72 uur</div>
                <div className="text-xs text-gray-500 mt-1">Tot shortlist</div>
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 md:p-6 text-center">
                <Building2 size={20} className="text-[#00897B] mx-auto mb-2" />
                <div className="text-sm md:text-base font-bold text-gray-900">500+</div>
                <div className="text-xs text-gray-500 mt-1">Professionals in netwerk</div>
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 md:p-6 text-center">
                <MapPin size={20} className="text-[#00897B] mx-auto mb-2" />
                <div className="text-sm md:text-base font-bold text-gray-900">{loc.provincie}</div>
                <div className="text-xs text-gray-500 mt-1">Regio</div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OVER DE MARKT */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              De arbeidsmarkt voor Finance en HR in {loc.stad}
            </h2>
            <p className="text-gray-700 leading-relaxed text-base md:text-lg">{loc.overDeMarkt}</p>
          </FadeIn>
        </div>
      </section>

      {/* SECTOREN */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Sectoren in {loc.stad} en omgeving
            </h2>
            <p className="text-gray-700 leading-relaxed text-base md:text-lg mb-8">{loc.sectorenIntro}</p>
          </FadeIn>
          <div className="grid sm:grid-cols-2 gap-3">
            {loc.sectoren.map((s, i) => (
              <FadeIn key={i} delay={i * 0.05}>
                <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle size={18} className="text-[#00897B] flex-shrink-0 mt-0.5" />
                  <h3 className="text-sm md:text-base text-gray-800 font-medium leading-snug">{s}</h3>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WAAROM */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Waarom kiezen voor Staffing Force in {loc.stad}?
            </h2>
            <p className="text-gray-700 leading-relaxed text-base md:text-lg">{loc.waaromStaffingForce}</p>
          </FadeIn>
        </div>
      </section>

      {/* BIJZONDERHEDEN */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Wat maakt {loc.stad} bijzonder voor onze opdrachten?
            </h2>
            <p className="text-gray-700 leading-relaxed text-base md:text-lg">{loc.bijzonderheden}</p>
          </FadeIn>
        </div>
      </section>

      {/* SPECIALISMEN */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
              Specialismen die wij in {loc.stad} invullen
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-4">
            <Link
              to="/specialisme/interim-financial-controller"
              className="group block bg-gray-50 hover:bg-[#00897B]/5 border border-gray-100 hover:border-[#00897B]/30 rounded-xl p-6 transition-all"
            >
              <span className="text-[#00897B] text-xs font-semibold uppercase tracking-widest">Finance</span>
              <h3 className="font-bold text-gray-900 mt-2 mb-2 group-hover:text-[#00897B] transition-colors">
                Interim Financial Controller in {loc.stad}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Doorlooptijden en sectoren voor financial controllers op interim basis.
              </p>
              <span className="text-[#00897B] font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Bekijk specialisme <ArrowRight size={14} />
              </span>
            </Link>
            <Link
              to="/specialisme/hr-business-partner"
              className="group block bg-gray-50 hover:bg-[#00897B]/5 border border-gray-100 hover:border-[#00897B]/30 rounded-xl p-6 transition-all"
            >
              <span className="text-[#00897B] text-xs font-semibold uppercase tracking-widest">HR</span>
              <h3 className="font-bold text-gray-900 mt-2 mb-2 group-hover:text-[#00897B] transition-colors">
                HR Business Partner in {loc.stad}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                Detachering en werving van senior HR Business Partners voor groeibedrijven.
              </p>
              <span className="text-[#00897B] font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Bekijk specialisme <ArrowRight size={14} />
              </span>
            </Link>
            <Link
              to="/specialisme/csrd-consultant"
              className="group block bg-gray-50 hover:bg-[#00897B]/5 border border-gray-100 hover:border-[#00897B]/30 rounded-xl p-6 transition-all"
            >
              <span className="text-[#00897B] text-xs font-semibold uppercase tracking-widest">
                Duurzaamheid
              </span>
              <h3 className="font-bold text-gray-900 mt-2 mb-2 group-hover:text-[#00897B] transition-colors">
                CSRD consultant in {loc.stad}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                ESG, EU Taxonomy en CSRD rapportage door ervaren consultants.
              </p>
              <span className="text-[#00897B] font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Bekijk specialisme <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Veelgestelde vragen over detachering in {loc.stad}
            </h2>
            <p className="text-gray-700 leading-relaxed text-base md:text-lg mb-10">
              Hieronder de meest gestelde vragen die wij krijgen van opdrachtgevers en kandidaten in
              {' '}{loc.stad} en omgeving. Staat uw vraag er niet bij? Neem dan{' '}
              <Link to="/contact" className="text-[#00897B] font-semibold hover:underline">
                contact
              </Link>{' '}
              met ons op.
            </p>
          </FadeIn>
          <div className="space-y-3">
            {loc.faqs.map((f, i) => (
              <FadeIn key={i} delay={i * 0.04}>
                <FaqItem
                  vraag={f.vraag}
                  antwoord={f.antwoord}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.4}>
            <div className="mt-10 text-center">
              <Link
                to="/veelgestelde-vragen"
                className="inline-flex items-center gap-2 text-[#00897B] font-semibold hover:gap-3 transition-all"
              >
                Bekijk alle veelgestelde vragen <ArrowRight size={16} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* INTERNE LINKS */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Meer informatie over onze dienstverlening
            </h2>
            <p className="text-gray-700 leading-relaxed text-base md:text-lg mb-8">
              Op de onderstaande pagina's leest u meer over onze specialismen, vacatures en aanpak.
              Zo krijgt u een compleet beeld van wat Staffing Force voor uw organisatie in {loc.stad}
              {' '}kan betekenen.
            </p>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {loc.interneLinks.map((link, i) => (
              <FadeIn key={i} delay={i * 0.04}>
                <Link
                  to={link.path}
                  className="group flex items-center gap-3 bg-gray-50 hover:bg-[#00897B]/5 border border-gray-100 hover:border-[#00897B]/40 rounded-xl p-4 transition-all"
                >
                  <Link2 size={16} className="text-[#00897B] flex-shrink-0" />
                  <span className="text-sm text-gray-800 group-hover:text-[#00897B] font-medium leading-snug transition-colors">
                    {link.label}
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ANDERE LOCATIES */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Detachering in andere steden
            </h2>
            <p className="text-gray-700 leading-relaxed text-base md:text-lg mb-8">
              Naast {loc.stad} is Staffing Force actief in 19 andere steden in heel Nederland.
              Bekijk de marktinformatie en onze aanpak per locatie.
            </p>
          </FadeIn>
          <div className="flex flex-wrap gap-2">
            {locaties
              .filter((l) => l.slug !== loc.slug)
              .map((l, i) => (
                <FadeIn key={l.slug} delay={i * 0.01}>
                  <Link
                    to={`/locaties/${l.slug}`}
                    className="inline-flex items-center gap-1.5 bg-white hover:bg-[#00897B]/5 border border-gray-100 hover:border-[#00897B]/30 text-gray-700 hover:text-[#00897B] text-sm font-medium px-3 py-2 rounded-lg transition-all"
                  >
                    <MapPin size={13} />
                    {l.stad}
                  </Link>
                </FadeIn>
              ))}
            <Link
              to="/locaties"
              className="inline-flex items-center gap-1.5 bg-[#00897B] hover:bg-[#00796B] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all"
            >
              Alle locaties <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#0d1b2a]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Finance of HR professional nodig in {loc.stad}?
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto text-sm md:text-base">
              Vertel ons wat u zoekt. Binnen één werkdag heeft u een eerste reactie van ons team in
              Amsterdam, en binnen 48 tot 72 uur een shortlist met passende kandidaten voor {loc.stad}.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/opdracht-opgeven"
                className="bg-[#00897B] hover:bg-[#00796B] text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              >
                Opdracht opgeven <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white/30 hover:border-white text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center justify-center"
              >
                Neem contact op
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default LocatiePage;
